import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  isEmptyCollection,
  useApplicationState,
  useCasinoGamesState,
  useCasinoNewState,
  useCategoryGamesState,
} from '@atlas/core';
import { Loader, ViewportDisplayController } from '@atlas/ui';

import { CasinoCategory } from 'components/Casino/components/CasinoCategory';
import CasinoLoadMoreButton from 'components/Casino/components/CasinoLoadMoreButton';
import GamesInfiniteLoader from 'components/Casino/components/GamesInfiniteLoader';

const SEARCH_CATEGORIES_COUNT = 2;

const placeholderStyles = { height: 236 };
const CasinoSearchCategoryPlaceholder = React.forwardRef((props, ref) => {
  return <div className={props.className} ref={ref} style={placeholderStyles} />;
});

CasinoSearchCategoryPlaceholder.propTypes = {
  className: PropTypes.string,
};

export const CasinoCategoryGames = ({ setActiveItem }) => {
  const { currentSlice, games, hasNext, loadMore, loaded, loading } = useCasinoGamesState();
  const { authenticated } = useApplicationState();
  const { identifyFavoriteGames } = useCasinoNewState();

  const handleLoadMore = useCallback(() => {
    loadMore(currentSlice + 1);
  }, [currentSlice, loadMore]);

  useEffect(() => {
    if (!authenticated || isEmptyCollection(games)) return;

    const gameIds = games.map(({ id }) => id);

    identifyFavoriteGames(gameIds);
  }, [games, authenticated]);

  return (
    <div className="casino__games--list-wrapper">
      <GamesInfiniteLoader
        games={games}
        hasLoadMore={hasNext}
        loading={loading || !loaded}
        setActiveItem={setActiveItem}
      />
      {hasNext && <CasinoLoadMoreButton handleClick={handleLoadMore} />}
    </div>
  );
};

CasinoCategoryGames.propTypes = {
  setActiveItem: PropTypes.func,
};

export const CasinoSearchCategoryGames = ({ showCategoryHandler, sliderSettings }) => {
  const { categoryGames, loading } = useCategoryGamesState();
  const { authenticated } = useApplicationState();
  const { identifyFavoriteGames } = useCasinoNewState();

  const searchCategoryGames = useMemo(() => {
    return [...categoryGames]
      .sort((a, b) => a.position - b.position)
      .slice(0, SEARCH_CATEGORIES_COUNT);
  }, [categoryGames]);

  useEffect(() => {
    if (!authenticated) return;

    let gameIds = [];

    searchCategoryGames.forEach(({ games }) => {
      gameIds = gameIds.concat(games.map(({ id }) => id));
    });

    if (gameIds.length) {
      identifyFavoriteGames(gameIds);
    }
  }, [searchCategoryGames, authenticated]);

  if (loading) return <Loader />;

  return (
    <>
      {searchCategoryGames.map(category => (
        <ViewportDisplayController
          className="casino__category placeholder"
          key={category.id}
          placeholder={CasinoSearchCategoryPlaceholder}
        >
          <CasinoCategory
            category={category}
            showCategoryHandler={showCategoryHandler}
            sliderSettings={sliderSettings}
          />
        </ViewportDisplayController>
      ))}
    </>
  );
};

CasinoSearchCategoryGames.propTypes = {
  showCategoryHandler: PropTypes.func,
  sliderSettings: PropTypes.arrayOf(PropTypes.object),
};
